import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Job } from 'containers/jobs/jobs.model';
import RelatedInvoiceCard from 'containers/invoices/components/RelatedInvoiceCard';
import RelatedEstimateCard from 'containers/estimates/components/RelatedEstimateCard';
import { If } from 'components';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import ProfitabilitySummary, { ProfitabilitySummaryData } from 'containers/jobs/components/ProfitabilitySummary';
import { CreateEditJobNavEnum, JobStatus } from 'containers/jobs/constant';
import routesPath from 'routes/RoutesPath';
import { useCommunicationsControl } from 'containers/communications';
import { useLocation, useNavigate } from 'react-router-dom';
import BillingTabJobLineItemSection from 'containers/jobs/job-details/components/BillingTabJobLineItemSection';
import ExternalCompanyReferralTable from './ExternalComapanyReferralTable';
import { AddIconWoPadding } from 'assets/icons';
import CommissionsTable from './CommissionsTable';
import {
  useDeleteJobExternalCompanyMutation,
  useEditJobExternalCompanyReferralMutation,
  useLazyGetJobCommissionSplitQuery
} from 'containers/jobs/api';
import ExpensesTable from './ExpensesTable';
import {
  useGetJobAdjustmentsQuery,
  useUpdateAdjustmentStatusMutation,
  useUpdateJobAdjustmentMutation
} from 'containers/accounting/pay-runs/api';
import { PayAdjustmentsTable } from 'containers/accounting/pay-runs/components/PayAdjustmentsTable';
import PayAdjustmentBanner from 'containers/accounting/pay-runs/components/PayAdjustmentBanner';
import { AdjustmentSourceType, AdjustmentStatus } from 'containers/accounting/pay-runs/constants';
import { AdjustmentFormData, GetSuggestedAdjustmentsForJobResponse } from 'containers/accounting/pay-runs/payrun.model';
// eslint-disable-next-line max-len
import ReviewAdjustmentSuggestionsModal from 'containers/accounting/pay-runs/components/ReviewAdjustmentSuggestionsModal';
import classNames from 'classnames';
import { selectJobLineItems } from 'containers/jobs/create-edit-job/createJobSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectCurrentTeam, selectCurrentTeamAccountingPermissions, selectIsOwner } from 'containers/app/appSlice';
import { JobPermissionsContext } from '../JobDetails';
import { checkIfInvoiceIsPaid } from 'containers/invoices/utils';
import { usePaginatedJobExpensesQuery } from '../hooks/usePaginatedJobExpensesQuery';
import { PayRunsCleverTapEvents } from 'containers/accounting/pay-runs/events';
import { logAnalyticEvent } from 'utils/analytics';
import { SubscriptionStatus } from 'containers/app/app.model';
import { resetEstimateState } from 'containers/estimates/estimatesSlice';
import { JobDetailsTabs } from '../constants';
import { StatusIndicator } from 'containers/invoices/constants';

interface BillingTabProps {
  job: Job;
  onInvoiceClick: (invoiceId: string) => void;
  onEstimateClick: (estimateId: string) => void;
  profitabilityData?: ProfitabilitySummaryData;
  isLoading?: boolean;
  suggestedAdjustments?: GetSuggestedAdjustmentsForJobResponse;
  isFetchingSuggestedAdjustments?: boolean;
  refetchSuggestedAdjustments?: () => any;
}

const BillingTab: FC<BillingTabProps> = ({
  job,
  onInvoiceClick,
  onEstimateClick,
  profitabilityData,
  isLoading,
  suggestedAdjustments,
  isFetchingSuggestedAdjustments,
  refetchSuggestedAdjustments
}) => {
  const navigate = useNavigate();
  const { allowAction: allowInvoiceCreate } = useCommunicationsControl('INVOICE_CREATE');
  const { allowAction: allowEstimateCreate } = useCommunicationsControl('ESTIMATE_CREATE');
  const currentTeamAccountingPermissions = useAppSelector(selectCurrentTeamAccountingPermissions);
  const {
    overrideJobRatesAndCommissions,
    managePayAdjustments,
    viewJobProfitability,
    createExpenses,
    manageOthersExpenses,
    viewMyJobCommissionsAndPay
  } = currentTeamAccountingPermissions ?? {};
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isOwner = useAppSelector(selectIsOwner);

  const dispatch = useAppDispatch();
  const location = useLocation();

  const { canEditJobs, canViewJobSource, canEditJobSource } = useContext(JobPermissionsContext);

  const { data: adjustments, refetch: refetchAdjustments } = useGetJobAdjustmentsQuery({
    teamId: job?.teamId,
    jobId: job.id
  });

  const [editExternalCompanyReferral, { isLoading: isEditingReferral }] = useEditJobExternalCompanyReferralMutation();
  const [deleteExternalCompany, { isLoading: isDeletingExternalCompany }] = useDeleteJobExternalCompanyMutation();
  const [getJobCommissionSplit, { data: commissionData }] = useLazyGetJobCommissionSplitQuery();
  const [updateAdjustment, { isLoading: isUpdatingAdjustment }] = useUpdateJobAdjustmentMutation();
  const [updateAdjustmentStatus, { isLoading: isUpdatingAdjustmentStatus }] = useUpdateAdjustmentStatusMutation();

  const {
    list: expenses,
    loadMore,
    hasMore,
    summary: expensesSummary,
    isFetching,
    totalListCount,
    resetRecallQuery: resetExpensesRecallQuery,
    hasPermissionsToView: hasPermissionsToViewExpenses
  } = usePaginatedJobExpensesQuery({
    jobId: job.id
  });

  const jobLineItemsState = useAppSelector(selectJobLineItems);

  const isPaidInvoice =
    job.invoices?.length > 0 &&
    job.invoices.some(invoice => checkIfInvoiceIsPaid(invoice.statusIndicator as StatusIndicator));

  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [currentSuggestionIndex, setCurrentSuggestionIndex] = useState(0);
  const [suggestionsUpdated, setSuggestionsUpdated] = useState(false);

  // Commission data processing
  const commissionList = useMemo(() => {
    return commissionData?.data || [];
  }, [commissionData]);

  // Fetch commission data
  useEffect(() => {
    getJobCommissionSplit({ teamId: job.teamId, jobId: job.id });
  }, [job.id, job.teamId, getJobCommissionSplit]);

  useEffect(() => {
    loadMore();
  }, [job.id, job.teamId]);

  const handleAddInvoiceFromJob = () => {
    if (!allowInvoiceCreate()) return;
    navigate(routesPath.INVOICES_CREATE + `?jobId=${job.id}`, {
      state: {
        returnTo: location.pathname,
        returnToState: {
          selectedJobTab: JobDetailsTabs.BILLING
        }
      }
    });
  };

  const handleAddEstimateFromJob = () => {
    if (
      currentTeam?.subscriptionStatus === SubscriptionStatus.SUBSCRIPTION_EXPIRED ||
      currentTeam?.subscriptionStatus === SubscriptionStatus.NOT_SUBSCRIBED
    ) {
      if (isOwner) navigate(routesPath.ESTIMATE_INTEGRATION);
      else navigate(routesPath.ESTIMATES);
      return;
    }
    if (!allowEstimateCreate()) return;
    dispatch(resetEstimateState());
    navigate(routesPath.ESTIMATES_CREATE + `?jobId=${job.id}`, {
      state: {
        returnTo: location.pathname,
        returnToState: {
          selectedJobTab: JobDetailsTabs.BILLING
        }
      }
    });
  };

  const handleExternalCompanyEditAndSave = async (amount: number) => {
    await editExternalCompanyReferral({
      teamId: job.teamId,
      jobId: job.id,
      amount,
      isEdited: true,
      externalCompanyId: job.externalCompany?.id || ''
    });
  };

  const handleExternalCompanyDelete = async () => {
    await deleteExternalCompany({
      teamId: job.teamId,
      jobId: job.id
    });
  };

  const handleExternalCompanyReset = async () => {
    await editExternalCompanyReferral({
      teamId: job.teamId,
      jobId: job.id,
      amount: job.externalCompany?.referralAmount || 0,
      externalCompanyId: job.externalCompany?.id || '',
      isEdited: false
    });
  };

  const handleReviewClick = () => {
    setCurrentSuggestionIndex(0);
    setIsReviewModalOpen(true);
    logAnalyticEvent(PayRunsCleverTapEvents.webPayRunAdjustmentReviewClick, {
      payRunId: job.id,
      merchantId: job.merchantId,
      source: 'job-details-billing-tab'
    });
  };

  const handleDiscard = async (adjustmentId: string) => {
    await updateAdjustmentStatus({
      adjustmentId,
      status: AdjustmentStatus.REJECTED,
      teamId: job.teamId
    });
    handleNextSuggestion();
  };

  const handleDecideLater = async (adjustmentData: AdjustmentFormData, adjustmentId: string, isEdited: boolean) => {
    if (isEdited) {
      await updateAdjustment({
        adjustmentId,
        teamId: job.teamId,
        jobId: job.id,
        payload: {
          teamId: job.teamId,
          jobId: job.id,
          ...adjustmentData
        }
      });
    }
    await updateAdjustmentStatus({
      adjustmentId,
      status: AdjustmentStatus.REMIND_ME_LATER,
      teamId: job.teamId
    });
    handleNextSuggestion();
  };

  const handleAcceptSuggestedAdjustment = async (
    adjustmentData: AdjustmentFormData,
    adjustmentId: string,
    isEdited: boolean
  ) => {
    if (isEdited) {
      await updateAdjustment({
        adjustmentId,
        teamId: job.teamId,
        jobId: job.id,
        payload: {
          teamId: job.teamId,
          jobId: job.id,
          ...adjustmentData
        }
      });
    }
    await updateAdjustmentStatus({
      adjustmentId,
      status: AdjustmentStatus.ACCEPTED,
      teamId: job.teamId
    });
    handleNextSuggestion();
  };

  const refetch = () => {
    refetchAdjustments();
    if (refetchSuggestedAdjustments) {
      refetchSuggestedAdjustments();
    }
    setSuggestionsUpdated(false);
  };

  const handleNextSuggestion = async () => {
    if (!refetchSuggestedAdjustments) return;

    const resp = await refetchSuggestedAdjustments();
    const adjustmentsData = resp?.data?.adjustments;

    if (adjustmentsData?.length === 0 || !adjustmentsData) {
      setIsReviewModalOpen(false);
      setCurrentSuggestionIndex(0);
      refetch();
      return;
    }
    setSuggestionsUpdated(true);
    if (suggestedAdjustments && currentSuggestionIndex < suggestedAdjustments.adjustments.length - 1) {
      setCurrentSuggestionIndex(prev => prev + 1);
    } else {
      setIsReviewModalOpen(false);
      setCurrentSuggestionIndex(0);
      refetch();
    }
  };

  const showNoLinkedDocuments = !job?.latestInvoice && (!job?.estimates || job.estimates.length === 0) && !canEditJobs;

  return (
    <div className="flex grow flex-col gap-6 p-6 text-primaryText">
      <If condition={suggestedAdjustments?.totalCount > 0 && managePayAdjustments}>
        <PayAdjustmentBanner
          isLoading={isFetchingSuggestedAdjustments}
          suggestionsCount={suggestedAdjustments?.totalCount || 0}
          onReviewClick={handleReviewClick}
        />
      </If>
      <div className="flex flex-col gap-4">
        <div className="text-17px font-bold">Related documents</div>

        <div className="flex max-w-[722px] gap-6">
          {job.latestInvoice ? (
            <div className="w-1/2">
              <RelatedInvoiceCard
                invoice={job.latestInvoice}
                onInvoiceClick={onInvoiceClick}
                fromBillingTab={true}
                className="!rounded-md"
                showPaymentDetails={true}
                paymentInfo={job.paymentInfo}
              />
            </div>
          ) : (
            canEditJobs && (
              <div
                className="flex w-1/2 cursor-pointer items-center justify-center rounded-md border 
          border-borderGray p-3 pr-4 hover:bg-secondaryBtn"
                role="presentation"
                onClick={handleAddInvoiceFromJob}>
                <div className="flex items-center">
                  <AddIconWoPadding className="mr-2 shrink-0" />
                  <div className="text-sbase font-semibold text-primaryBtn">Add invoice</div>
                </div>
              </div>
            )
          )}

          <If condition={!!job.estimates?.[0]}>
            <div className="w-1/2">
              <RelatedEstimateCard
                estimate={job.estimates?.[0]}
                onEstimateClick={onEstimateClick}
                fromBillingTab={true}
                className="!rounded-md"
                showExpiryDate={true}
              />
            </div>
          </If>

          <If condition={!job?.latestInvoice && (!job?.estimates || job.estimates.length === 0) && canEditJobs}>
            <div
              className="flex w-1/2 cursor-pointer items-center justify-center rounded-md border 
                    border-borderGray p-3 pr-4 hover:bg-secondaryBtn"
              role="presentation"
              onClick={handleAddEstimateFromJob}>
              <div className="flex items-center">
                <AddIconWoPadding className="mr-2 shrink-0" />
                <div className="text-sbase font-semibold text-primaryBtn">Add estimate</div>
              </div>
            </div>
          </If>

          {showNoLinkedDocuments && <div className="text-sbase text-primaryText">-</div>}
        </div>
      </div>
      <hr className="w-full border-secondaryBtn" />

      <If condition={jobLineItemsState?.length > 0 && viewJobProfitability}>
        <div>
          <div className="mb-3 text-17px font-bold text-primaryText">Profitability summary</div>
          <ProfitabilitySummary data={profitabilityData} isPaidInvoice={isPaidInvoice} isLoading={isLoading} />
        </div>
        <hr className="w-full border-secondaryBtn" />
      </If>

      <div className="relative flex flex-col gap-2">
        {canEditJobs && !isPaidInvoice && (
          <div className="absolute right-0">
            <CustomButton
              id="edit-line-items-button"
              type={ButtonType.SECONDARY}
              className="h-auto border-none p-0 text-secondary shadow-none hover:bg-white"
              onClick={() =>
                navigate(routesPath.EDIT_JOB.replace(':jobId', job.id), {
                  state: {
                    selectedJobTab: CreateEditJobNavEnum.LINE_ITEMS
                  }
                })
              }>
              Edit line items
            </CustomButton>
          </div>
        )}
        <div>
          <BillingTabJobLineItemSection job={job} />
        </div>
        {(createExpenses || manageOthersExpenses) && (
          <div>
            <div className="pb-3 text-17px font-bold text-primaryText">
              {manageOthersExpenses ? 'Expenses' : 'My expenses'}{' '}
              {totalListCount > 0 && <span className="">({totalListCount})</span>}
            </div>
            <ExpensesTable
              jobId={job.id}
              expenses={expenses || []}
              loadMore={loadMore}
              hasMore={hasMore}
              isFetching={isFetching}
              summary={expensesSummary}
              resetRecallQuery={resetExpensesRecallQuery}
              jobStatus={job.status}
              hasPermissionsToView={hasPermissionsToViewExpenses}
              isJobEditable={job.isEditable}
              jobMembers={job.assignees}
            />
          </div>
        )}
      </div>
      <hr className="w-full border-secondaryBtn" />

      {canViewJobSource && (
        <>
          <div className="flex flex-col gap-3">
            <div className="text-[17px] font-bold">
              External company referral{' '}
              <If condition={job.status !== JobStatus.DONE_PENDING_APPROVAL && job.status !== JobStatus.DONE}>
                <span>(estimated)</span>
              </If>
            </div>
            <div>
              <ExternalCompanyReferralTable
                jobNumber={job.jobNumber}
                referralData={job?.externalCompany}
                onEditAndSave={handleExternalCompanyEditAndSave}
                onDelete={handleExternalCompanyDelete}
                isLoading={isDeletingExternalCompany || isEditingReferral}
                onReset={handleExternalCompanyReset}
                jobStatus={job.status}
                isJobEditable={job.isReferralEditable && canEditJobSource}
              />
            </div>
          </div>
          <hr className="w-full border-secondaryBtn" />
        </>
      )}
      {(overrideJobRatesAndCommissions || viewMyJobCommissionsAndPay) && (
        <>
          <div className="relative">
            <div
              className={classNames('text-17px font-bold', {
                'absolute top-0 pt-1 mb-3':
                  (job.status === JobStatus.DONE || job.status === JobStatus.DONE_PENDING_APPROVAL) &&
                  overrideJobRatesAndCommissions &&
                  commissionList.length > 0 &&
                  job?.isEditable
              })}>
              {overrideJobRatesAndCommissions ? 'Commissions' : 'My commissions'}{' '}
              <If condition={job.status !== JobStatus.DONE_PENDING_APPROVAL && job.status !== JobStatus.DONE}>
                <span>(estimated)</span>
              </If>
            </div>
            <CommissionsTable job={job} commissionData={commissionList} />
          </div>
          <hr className="w-full border-secondaryBtn" />
        </>
      )}

      {(managePayAdjustments || viewMyJobCommissionsAndPay) && (
        <div className="flex flex-col gap-3">
          <div className="text-[17px] font-bold">{managePayAdjustments ? 'Pay adjustments' : 'My pay adjustments'}</div>
          <div className="flex flex-col gap-2">
            <If condition={suggestedAdjustments?.totalCount > 0 && managePayAdjustments}>
              <PayAdjustmentBanner
                isLoading={isFetchingSuggestedAdjustments}
                suggestionsCount={suggestedAdjustments?.totalCount || 0}
                onReviewClick={handleReviewClick}
              />
            </If>
            <PayAdjustmentsTable
              adjustments={adjustments?.adjustments || []}
              job={job}
              refetchAdjustments={refetchAdjustments}
            />
          </div>
        </div>
      )}
      <If condition={isReviewModalOpen && suggestedAdjustments && suggestedAdjustments.adjustments?.length > 0}>
        <ReviewAdjustmentSuggestionsModal
          open={isReviewModalOpen}
          onClose={() => {
            setIsReviewModalOpen(false);
            setCurrentSuggestionIndex(0);
            if (suggestionsUpdated) {
              refetch();
            }
          }}
          suggestedAdjustment={suggestedAdjustments?.adjustments?.[currentSuggestionIndex]}
          onDiscard={handleDiscard}
          onDecideLater={handleDecideLater}
          onAddAdjustment={handleAcceptSuggestedAdjustment}
          currentIndex={currentSuggestionIndex}
          totalSuggestions={suggestedAdjustments?.totalCount || 0}
          assignees={job.assignees}
          sourceType={AdjustmentSourceType.JOB}
          isLoading={isUpdatingAdjustment || isUpdatingAdjustmentStatus || isFetchingSuggestedAdjustments}
        />
      </If>
    </div>
  );
};

export default BillingTab;
